import React from "react";
import {
  UseFormRegister,
  FieldArrayWithId,
  FieldErrors,
  UseFormSetValue,
} from "react-hook-form";
import { Trash2, Plus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Button } from "components/ui/button";
import { StoreCombobox } from "../../components/store-combobox";
import { type CreateGamificationFormData } from "../../validations/gamification.validation";

interface StoresCardProps {
  register: UseFormRegister<CreateGamificationFormData>;
  setValue: UseFormSetValue<CreateGamificationFormData>;
  storeFields: FieldArrayWithId<
    CreateGamificationFormData,
    "stores",
    "id"
  >[];
  storeOptionsWithAll: Array<{ value: string; label: string }>;
  allStoresSelected: boolean;
  isLoadingStores: boolean;
  errors: FieldErrors<CreateGamificationFormData>;
  onAddStore: (storeCode: string) => void;
  onRemoveStore: (index: number) => void;
  storeCodesInput?: string;
  setStoreCodesInput?: (value: string) => void;
  onAddStoresByCodes?: () => void;
  comboboxOptions?: Array<{ value: string; label: string }>;
}

export const StoresCard: React.FC<StoresCardProps> = ({
  register,
  setValue,
  storeFields,
  storeOptionsWithAll,
  allStoresSelected,
  isLoadingStores,
  errors,
  onAddStore,
  onRemoveStore,
  storeCodesInput = "",
  setStoreCodesInput,
  onAddStoresByCodes,
  comboboxOptions = [],
}) => {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle>Lojas Participantes</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <Label>Adicionar Loja *</Label>
          <StoreCombobox
            options={storeOptionsWithAll}
            onSelect={onAddStore}
            placeholder="Selecione uma loja"
            searchPlaceholder="Buscar loja..."
            emptyMessage="Nenhuma loja disponível para seleção"
            loading={isLoadingStores}
            disabled={allStoresSelected}
          />
        </div>

        {setStoreCodesInput && onAddStoresByCodes && (
          <div>
            <Label>Adicionar Lojas por Código</Label>
            <div className="flex gap-3">
              <Input
                placeholder="Ex: 1;5;23;12"
                value={storeCodesInput}
                onChange={(e) => setStoreCodesInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    onAddStoresByCodes();
                  }
                }}
                disabled={comboboxOptions.length === 0 || isLoadingStores || allStoresSelected}
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                onClick={onAddStoresByCodes}
                disabled={!storeCodesInput.trim() || comboboxOptions.length === 0 || isLoadingStores || allStoresSelected}
              >
                <Plus className="h-4 w-4 mr-2" />
                Incluir
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Digite os códigos das lojas separados por ponto e vírgula (;)
            </p>
          </div>
        )}

        {allStoresSelected ? (
          <div className="space-y-3">
            <Label>Lojas Selecionadas</Label>
            <div className="p-4 border rounded-lg bg-green-50 border-green-200">
              <div className="flex items-center gap-2">
                <span className="text-2xl">🏪</span>
                <div>
                  <p className="font-semibold text-green-800">
                    Todas as Lojas Selecionadas
                  </p>
                  <p className="text-sm text-green-600">
                    A campanha será aplicada a todas as lojas do sistema
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setValue("stores", []);
                    setValue("allStoresSelected", false);
                  }}
                  className="ml-auto"
                >
                  Remover
                </Button>
              </div>
            </div>
          </div>
        ) : (
          storeFields.length > 0 && (
            <div className="space-y-3">
              <Label>Lojas Selecionadas</Label>
              {storeFields.map((field, index) => (
                <div
                  key={field.id}
                  className="grid grid-cols-1 md:grid-cols-12 gap-6 p-4 border rounded-lg bg-gray-50"
                >
                  <div className="md:col-span-1">
                    <Label htmlFor={`stores.${index}.storeCode`}>Código</Label>
                    <Input
                      id={`stores.${index}.storeCode`}
                      {...register(`stores.${index}.storeCode`)}
                      readOnly
                      className="bg-gray-100 text-center font-mono text-sm w-20 mx-auto"
                    />
                  </div>

                  <div className="md:col-span-4">
                    <Label htmlFor={`stores.${index}.nickname`}>
                      Nome da Loja
                    </Label>
                    <Input
                      id={`stores.${index}.nickname`}
                      {...register(`stores.${index}.nickname`)}
                      placeholder="Nome personalizado da loja"
                      className="bg-white"
                    />
                    {errors.stores?.[index]?.nickname?.message && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.stores[index]?.nickname?.message}
                      </p>
                    )}
                  </div>

                  <div className="md:col-span-6">
                    <Label htmlFor={`stores.${index}.storeName`}>
                      Endereço
                    </Label>
                    <Input
                      id={`stores.${index}.storeName`}
                      {...register(`stores.${index}.storeName`)}
                      placeholder="Digite o endereço completo da loja (rua, número, bairro, cidade)"
                      className="bg-white"
                    />
                    {errors.stores?.[index]?.storeName?.message && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.stores[index]?.storeName?.message}
                      </p>
                    )}
                  </div>

                  <div className="md:col-span-1 flex items-end">
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => onRemoveStore(index)}
                      className="w-full"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {errors.stores && (
          <p className="text-sm text-red-500 mt-1">{errors.stores.message}</p>
        )}
      </CardContent>
    </Card>
  );
};
